import React, { useMemo } from 'react';
import CommonFilter from '../components/CommonFilter';
import SteamFeedsReviewsDetailed from '../components/SteamFeedsReviewsDetailed';
import useGetPcConsoleStats from '@op/pages/gamestore/hooks/useGetPcConsoleStats';
import useGetPcConsoleTimeline from '@op/pages/gamestore/hooks/useGetPcConsoleTimeline';
import SteamAllReviews from './SteamAllReviews';
import SteamFilteredReviews from './SteamFilteredReviews';
import steamPerformancePoint from '@op/operationLog/pages/steamPerformanceSteamRatings';
import SteamSupportLanguage from './SteamSupportLanguage';
import { notification } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
const defaultChannel: any = ['steam'];

const localNotificationKey = 'DBO_STEAM_RATING_NOTIFICATION';

export default function SteamRatings() {
  const { formatMessage } = useLanguage();
  const tipsText = formatMessage(
    'After selecting the hour filter, the newly added hour section is set to move up by default.',
  );

  return (
    <CommonFilter
      steamPerformancePoint={steamPerformancePoint}
      title={'Steam Ratings'}
      tipsText={tipsText}
      confirmCallback={({ isTrueGame, value }) => {
        const hasNotification =
          localStorage.getItem(localNotificationKey) === '1';

        if (!hasNotification && isTrueGame && value.date_type === 'hourly') {
          notification.warning({
            duration: 3000,
            unique: true,
            description: tipsText,
            className: 'dbo-steam-rating-notification-wrapper',
          });
          localStorage.setItem(localNotificationKey, '1');
        }
      }}
    >
      {({ filter, wholeGameInfo, gameInfo, isNeedTips }: any) => (
        <ReviewsInfo
          filter={filter}
          wholeGameInfo={wholeGameInfo}
          gameInfo={gameInfo}
          isNeedTips={isNeedTips}
        ></ReviewsInfo>
      )}
    </CommonFilter>
  );
}

function ReviewsInfo({
  filter,
  wholeGameInfo,
  gameInfo,
  isNeedTips,
}: {
  filter: any;
  wholeGameInfo: any;
  gameInfo: any;
  isNeedTips?: boolean;
}) {
  const {
    userScoreTrendsLoading,
    userScoreTrendsApiError,
    globalTrendsTimeline,
  } = useGetPcConsoleTimeline({
    keepParams: filter,
    channel: defaultChannel,
  });

  const { userScore, userScoreLoading, userScoreApiError } =
    useGetPcConsoleStats({
      keepParams: filter,
      channel: defaultChannel,
      needLink: false,
    });

  const renderList = useMemo(() => {
    const list = [
      {
        element: (
          <SteamAllReviews
            userScore={userScore}
            filter={filter}
            wholeGameInfo={wholeGameInfo}
            loading={userScoreLoading}
            timeline={globalTrendsTimeline}
            apiErr={userScoreTrendsApiError}
            timelineLoading={userScoreTrendsLoading}
          ></SteamAllReviews>
        ),
        sort: 3,
      },
      {
        element: <SteamSupportLanguage filter={filter} gameInfo={gameInfo} />,
        sort: 4,
      },
      {
        element: (
          <SteamFilteredReviews
            userScore={userScore}
            filter={filter}
            loading={userScoreLoading}
            timeline={globalTrendsTimeline}
            apiErr={userScoreTrendsApiError}
            timelineLoading={userScoreTrendsLoading}
          ></SteamFilteredReviews>
        ),
        sort: 1,
      },
      {
        element: (
          <SteamFeedsReviewsDetailed
            filter={filter}
            isStacked
            statsDetailType="steam-performance-keyword-ratings"
            statsLanguageDetailType="steam-ratings-language"
          ></SteamFeedsReviewsDetailed>
        ),
        sort: 2,
      },
    ];

    if (isNeedTips) {
      const realList = [...list];
      realList.sort((a, b) => a.sort - b.sort);
      return realList;
    }

    return list;
  }, [
    filter,
    gameInfo,
    globalTrendsTimeline,
    userScore,
    userScoreLoading,
    userScoreTrendsApiError,
    userScoreTrendsLoading,
    wholeGameInfo,
    isNeedTips,
  ]);

  return (
    <>
      {renderList.map((item) => (
        <React.Fragment key={item.sort}>{item.element}</React.Fragment>
      ))}
    </>
  );
}
